import { KeepSelection } from "@/actions/selection/keep-selection"
import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import logger from "@/lib/logger/logger-init"
import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { getSha256 } from "@/lib/utils/hashing/hash"
import { Order } from "@/types/centra/checkout-api/payment/payment-reciept"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import { CheckoutAPISelectionWithRequiredToken } from "@/types/centra/checkout-api/selection/get-selection"
import { sendGTMEvent } from "@next/third-parties/google"
import {
    klaviyoAddedToCartEvent,
    klaviyoViewedProductEvent
} from "./klaviyoProductTracking"

const getGtmHash = (value: string | undefined | null): string | null =>
    (typeof value === "string" && getSha256(value)) || null

export const setUserPropertiesEvent = (country: string, language: string) => {
    const eventData = {
        event: "set_user_properties",
        site_country: country,
        site_language: language
    }
    logger.debug("sendGTMEvent", eventData)
    sendGTMEvent(eventData)
}

export const viewItemEvent = (
    product: CheckoutAPIProduct | RelatedProduct | ProductData,
    item: any
) => {
    const {
        price,
        priceAsNumber,
        sku,
        name,
        brandName,
        color_swatch,
        categoryName,
        variantName
    } = product || {}

    const eventData = {
        event: "view_item",
        ecommerce: {
            currency: price?.slice(-3), // Get three-letter currency code from the formatted price, i.e. "EUR" from "123.00 EUR"
            items: [
                {
                    item_id: item?.sku || sku,
                    item_name: name,
                    item_brand: brandName,
                    item_color: color_swatch?.color_name,
                    ...(categoryName?.[0] && { item_category: categoryName[0] }),
                    ...(categoryName?.[1] && { item_category2: categoryName[1] }),
                    ...(categoryName?.[2] && { item_category3: categoryName[2] }),
                    ...(categoryName?.[3] && { item_category4: categoryName[3] }),
                    ...(categoryName?.[4] && { item_category5: categoryName[4] }),
                    item_variant: variantName,
                    price: priceAsNumber
                }
            ]
        }
    }
    logger.debug("sendGTMEvent", eventData)
    sendGTMEvent(eventData)
    klaviyoViewedProductEvent(product, item)
}

export const addToCartEvent = (
    product: CheckoutAPIProduct | RelatedProduct | ProductData,
    item: any
) => {
    const {
        price,
        priceAsNumber,
        name,
        brandName,
        color_swatch,
        categoryName,
        variantName
    } = product || {}

    const eventData = {
        event: "add_to_cart",
        ecommerce: {
            currency: price?.slice(-3), // Get three-letter currency code from the formatted price, i.e. "EUR" from "123.00 EUR"
            value: priceAsNumber,
            items: [
                {
                    item_id: item.sku,
                    item_name: name,
                    item_brand: brandName,
                    item_color: color_swatch?.color_name,
                    item_size: item.size,
                    ...(categoryName?.[0] && { item_category: categoryName[0] }),
                    ...(categoryName?.[1] && { item_category2: categoryName[1] }),
                    ...(categoryName?.[2] && { item_category3: categoryName[2] }),
                    ...(categoryName?.[3] && { item_category4: categoryName[3] }),
                    ...(categoryName?.[4] && { item_category5: categoryName[4] }),
                    item_variant: variantName,
                    price: priceAsNumber,
                    quantity: 1
                }
            ]
        }
    }
    logger.debug("sendGTMEvent", eventData)
    sendGTMEvent(eventData)
    klaviyoAddedToCartEvent(product, item)
}

export const beginCheckoutEvent = (cart: KeepSelection | null) => {
    if (!cart) return

    const selection = cart?.checkoutApiSelection?.selection
    const items =
        selection?.items.map(item => {
            const { name, brandName, color_swatch, categoryName, variantName } =
                item.product

            return {
                item_id: item.sku,
                item_name: name,
                item_brand: brandName,
                item_color: color_swatch?.color_name,
                item_size: item.size,
                ...(categoryName?.[0] && { item_category: categoryName[0] }),
                ...(categoryName?.[1] && { item_category2: categoryName[1] }),
                ...(categoryName?.[2] && { item_category3: categoryName[2] }),
                ...(categoryName?.[3] && { item_category4: categoryName[3] }),
                ...(categoryName?.[4] && { item_category5: categoryName[4] }),
                item_variant: variantName,
                price: item.priceEachAsNumber,
                quantity: item.quantity
            }
        }) || []

    const {
        grandTotalPriceAsNumber = 0,
        grandTotalPriceTaxAsNumber = 0,
        shippingPriceAsNumber = 0
    } = selection?.totals ?? {}
    const value =
        grandTotalPriceAsNumber - shippingPriceAsNumber - grandTotalPriceTaxAsNumber

    const eventData = {
        event: "begin_checkout",
        ecommerce: {
            currency: selection?.currency,
            value,
            items
        }
    }
    logger.debug("sendGTMEvent", eventData)
    sendGTMEvent(eventData)
}

export const addShippingInfoEvent = (cart: KeepSelection | null) => {
    if (!cart) return

    const selection = cart?.checkoutApiSelection?.selection
    const items =
        selection?.items.map(item => {
            const { name, brandName, color_swatch, categoryName, variantName } =
                item.product

            return {
                item_id: item.sku,
                item_name: name,
                item_brand: brandName,
                item_color: color_swatch?.color_name,
                item_size: item.size,
                ...(categoryName?.[0] && { item_category: categoryName[0] }),
                ...(categoryName?.[1] && { item_category2: categoryName[1] }),
                ...(categoryName?.[2] && { item_category3: categoryName[2] }),
                ...(categoryName?.[3] && { item_category4: categoryName[3] }),
                ...(categoryName?.[4] && { item_category5: categoryName[4] }),
                item_variant: variantName,
                price: item.priceEachAsNumber,
                quantity: item.quantity
            }
        }) || []

    const coupon = selection?.discounts?.vouchers
        ?.map(voucher => voucher.voucher)
        .filter(voucherCode => voucherCode !== FREE_SHIPPING_VOUCHER_CODE)
        .join(", ")

    const {
        grandTotalPriceAsNumber = 0,
        grandTotalPriceTaxAsNumber = 0,
        shippingPriceAsNumber = 0
    } = selection?.totals ?? {}
    const value =
        grandTotalPriceAsNumber - shippingPriceAsNumber - grandTotalPriceTaxAsNumber
    const emailAddress = selection?.address.email || selection?.shippingAddress.email
    const firstName = selection?.address.firstName || selection?.shippingAddress.firstName
    const lastName = selection?.address.lastName || selection?.shippingAddress.lastName

    const eventData = {
        event: "add_shipping_info",
        ecommerce: {
            currency: selection?.currency,
            value,
            shipping_tier: selection?.shippingMethodName,
            shipping_city: selection?.shippingAddress?.city,
            coupon,
            items
        },
        hashed_email: getGtmHash(emailAddress),
        hashed_firstName: getGtmHash(firstName),
        hashed_lastName: getGtmHash(lastName)
    }
    logger.debug("sendGTMEvent", eventData)
    sendGTMEvent(eventData)
}

export const addPaymentInfoEvent = (
    data: CheckoutAPISelectionWithRequiredToken | Order | null | undefined
) => {
    if (!data) return

    let cartOrderData

    if ("selection" in data) {
        // is CheckoutAPISelectionWithRequiredToken
        cartOrderData = data?.selection
    } else {
        // is Order
        cartOrderData = data
    }

    const items =
        cartOrderData?.items.map(item => {
            const { name, brandName, color_swatch, categoryName, variantName } =
                item.product

            return {
                item_id: item.sku,
                item_name: name,
                item_brand: brandName,
                item_color: color_swatch?.color_name,
                item_size: item.size,
                ...(categoryName?.[0] && { item_category: categoryName[0] }),
                ...(categoryName?.[1] && { item_category2: categoryName[1] }),
                ...(categoryName?.[2] && { item_category3: categoryName[2] }),
                ...(categoryName?.[3] && { item_category4: categoryName[3] }),
                ...(categoryName?.[4] && { item_category5: categoryName[4] }),
                item_variant: variantName,
                price: item.priceEachAsNumber,
                quantity: item.quantity
            }
        }) || []

    const coupon = cartOrderData?.discounts?.vouchers
        ?.map(voucher => voucher.voucher)
        .filter(voucherCode => voucherCode !== FREE_SHIPPING_VOUCHER_CODE)
        .join(", ")

    const {
        grandTotalPriceAsNumber,
        grandTotalPriceTaxAsNumber: tax,
        shippingPriceAsNumber: shipping
    } = cartOrderData?.totals

    const value = grandTotalPriceAsNumber - shipping - tax
    const emailAddress =
        cartOrderData.address.email || cartOrderData.shippingAddress.email
    const firstName =
        cartOrderData.address.firstName || cartOrderData.shippingAddress.firstName
    const lastName =
        cartOrderData.address.lastName || cartOrderData.shippingAddress.lastName

    const eventData = {
        event: "add_payment_info",
        ecommerce: {
            currency: cartOrderData?.currency,
            value,
            payment_type: cartOrderData?.paymentMethodName,
            coupon,
            items
        },
        hashed_email: getGtmHash(emailAddress),
        hashed_firstName: getGtmHash(firstName),
        hashed_lastName: getGtmHash(lastName)
    }
    logger.debug("sendGTMEvent", eventData)
    sendGTMEvent(eventData)
}

export const purchaseEvent = (order: Order | undefined) => {
    if (!order) return
    const items =
        order?.items.map(item => {
            const { name, brandName, color_swatch, categoryName, variantName } =
                item.product

            return {
                item_id: item.sku,
                item_name: name,
                item_brand: brandName,
                item_color: color_swatch?.color_name,
                item_size: item.size,
                ...(categoryName?.[0] && { item_category: categoryName[0] }),
                ...(categoryName?.[1] && { item_category2: categoryName[1] }),
                ...(categoryName?.[2] && { item_category3: categoryName[2] }),
                ...(categoryName?.[3] && { item_category4: categoryName[3] }),
                ...(categoryName?.[4] && { item_category5: categoryName[4] }),
                item_variant: variantName,
                price: item.priceEachAsNumber,
                quantity: item.quantity
            }
        }) || []

    const coupon = order?.discounts?.vouchers
        ?.map(voucher => voucher.voucher)
        .filter(voucherCode => voucherCode !== FREE_SHIPPING_VOUCHER_CODE)
        .join(", ")

    const {
        grandTotalPriceAsNumber,
        grandTotalPriceTaxAsNumber: tax,
        shippingPriceAsNumber: shipping
    } = order?.totals

    const value = grandTotalPriceAsNumber - shipping - tax
    const emailAddress = order.address.email || order.shippingAddress.email
    const firstName = order.address.firstName || order.shippingAddress.firstName
    const lastName = order.address.lastName || order.shippingAddress.lastName

    const eventData = {
        event: "purchase",
        ecommerce: {
            currency: order?.currency,
            value,
            transaction_id: order?.order,
            shipping,
            tax,
            coupon,
            items
        },
        ecomm_country: order?.shippingAddress?.country,
        ecomm_language: order?.language,
        hashed_email: getGtmHash(emailAddress),
        hashed_firstName: getGtmHash(firstName),
        hashed_lastName: getGtmHash(lastName)
    }
    logger.debug("sendGTMEvent", eventData)
    sendGTMEvent(eventData)
}
